import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import audioDot from "../assets/images/icons/audio-dot.svg";
import audioPause from "../assets/images/icons/audio-pause.svg";
import audioPlay from "../assets/images/icons/audio-play.svg";
import audioTrack from "../assets/images/icons/audio-track.svg";
import stairsIcon from "../assets/images/icons/stairs-icon.svg";
import { getRoomAudioResult, recordListenedRange } from "../services/audioListening";
import { RoomId, useTranslation } from "./Localization";

export type AudioTrack = { src: string; title: string; icon?: "stairs" };

type ExhibitAudioPlayerProps = {
  title: string;
  audioSrc?: string;
  tracks?: AudioTrack[];
  roomId: RoomId;
};

type PlaybackObservation = { src: string; mediaTime: number; observedAt: number };

export default function ExhibitAudioPlayer({ title, audioSrc, tracks, roomId }: ExhibitAudioPlayerProps) {
  const playlist = useMemo<AudioTrack[]>(() => tracks?.length ? tracks : audioSrc ? [{ src: audioSrc, title }] : [], [audioSrc, title, tracks]);
  const playlistKey = playlist.map((track) => track.src).join("|");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const continuePlaybackRef = useRef(false);
  const playbackObservationRef = useRef<PlaybackObservation | null>(null);
  const [, refreshListeningResult] = useState(0);
  const { t } = useTranslation();
  const currentTrack = playlist[currentIndex];
  const currentTrackIcon = currentTrack?.icon === "stairs" ? stairsIcon : undefined;

  useEffect(() => {
    audioRef.current?.pause();
    setCurrentIndex(0);
    setCurrentTime(0);
    setPlaying(false);
    continuePlaybackRef.current = false;
    playbackObservationRef.current = null;
  }, [playlistKey]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack || !continuePlaybackRef.current) return;
    continuePlaybackRef.current = false;
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [currentIndex, currentTrack]);

  const rememberDuration = (src: string, duration: number) => {
    if (!Number.isFinite(duration) || duration <= 0) return;
    setDurations((current) => current[src] === duration ? current : { ...current, [src]: duration });
  };

  const readMetadata = (src: string) => (event: SyntheticEvent<HTMLAudioElement>) => {
    rememberDuration(src, event.currentTarget.duration);
  };

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (!audio.paused) {
      audio.pause();
      setPlaying(false);
      return;
    }
    if (audio.ended && currentIndex === playlist.length - 1) {
      if (playlist.length === 1) {
        audio.currentTime = 0;
        setCurrentTime(0);
        audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        return;
      }
      continuePlaybackRef.current = true;
      setCurrentIndex(0);
      setCurrentTime(0);
      return;
    }
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const durationValues = playlist.map((track) => durations[track.src] || 0);
  const durationsReady = durationValues.every((duration) => duration > 0);
  const totalDuration = durationValues.reduce((sum, duration) => sum + duration, 0);
  const elapsedBeforeCurrent = durationValues.slice(0, currentIndex).reduce((sum, duration) => sum + duration, 0);
  const currentDuration = durations[currentTrack?.src] || audioRef.current?.duration || 0;
  const progress = durationsReady && totalDuration > 0
    ? Math.min((elapsedBeforeCurrent + currentTime) / totalDuration, 1)
    : playlist.length ? Math.min((currentIndex + (currentDuration > 0 ? currentTime / currentDuration : 0)) / playlist.length, 1) : 0;

  let accumulatedDuration = 0;
  const boundaries = playlist.map((_, index) => {
    const boundary = durationsReady && totalDuration > 0 ? accumulatedDuration / totalDuration : index / playlist.length;
    accumulatedDuration += durationValues[index];
    return boundary;
  });

  const finishTrack = () => {
    if (currentIndex < playlist.length - 1) {
      continuePlaybackRef.current = true;
      setCurrentTime(0);
      setCurrentIndex((index) => index + 1);
    } else {
      setCurrentTime(currentDuration);
      setPlaying(false);
    }
  };

  const observePlayback = (audio: HTMLAudioElement) => {
    if (!currentTrack || audio.paused) return;
    const now = performance.now();
    const previous = playbackObservationRef.current;
    if (previous?.src === currentTrack.src) {
      const mediaDelta = audio.currentTime - previous.mediaTime;
      const realDelta = (now - previous.observedAt) / 1000;
      if (mediaDelta > 0 && mediaDelta <= realDelta + 0.75) {
        recordListenedRange(roomId, currentTrack.src, audio.duration, previous.mediaTime, audio.currentTime);
        refreshListeningResult((value) => value + 1);
      }
    }
    playbackObservationRef.current = { src: currentTrack.src, mediaTime: audio.currentTime, observedAt: now };
  };

  const resetPlaybackObservation = (audio: HTMLAudioElement) => {
    if (currentTrack) playbackObservationRef.current = { src: currentTrack.src, mediaTime: audio.currentTime, observedAt: performance.now() };
  };

  const selectTrack = (index: number) => {
    const audio = audioRef.current;
    const shouldContinue = Boolean(audio && !audio.paused);
    if (index === currentIndex) {
      if (!audio) return;
      audio.currentTime = 0;
      playbackObservationRef.current = null;
      setCurrentTime(0);
      if (shouldContinue) audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      return;
    }
    continuePlaybackRef.current = shouldContinue;
    playbackObservationRef.current = null;
    setCurrentTime(0);
    setCurrentIndex(index);
  };

  const listeningResult = getRoomAudioResult(roomId, playlist.map((track) => track.src));

  return <section className="exhibit-audio" aria-label={t("accessibility.audioSection", { title: currentTrack?.title || title })}>
    {currentTrack && <audio
      ref={audioRef}
      key={currentTrack.src}
      src={currentTrack.src}
      preload="metadata"
      onLoadedMetadata={(event) => { rememberDuration(currentTrack.src, event.currentTarget.duration); setCurrentTime(event.currentTarget.currentTime); }}
      onPlay={(event) => resetPlaybackObservation(event.currentTarget)}
      onSeeking={() => { playbackObservationRef.current = null; }}
      onTimeUpdate={(event) => { observePlayback(event.currentTarget); setCurrentTime(event.currentTarget.currentTime); }}
      onEnded={finishTrack}
    />}
    {playlist.length > 1 && playlist.map((track) => <audio className="audio-metadata" key={track.src} src={track.src} preload="metadata" onLoadedMetadata={readMetadata(track.src)} />)}
    <div className="exhibit-audio-title">
      {currentTrackIcon && <img src={currentTrackIcon} alt="" />}
      <p>{currentTrack?.title || title}</p>
    </div>
    <div className="audio-timeline">
      <button type="button" className={playing ? "is-playing" : ""} onClick={togglePlayback} aria-label={playing ? t("audio.pause") : t("audio.play")} aria-pressed={playing}>
        <img src={playing ? audioPause : audioPlay} alt="" />
      </button>
      <div className="audio-track">
        <img className="audio-track-main" src={audioTrack} alt="" />
        <span className="audio-track-progress" style={{ width: `${progress * 100}%` }} />
        {playlist.length > 1 && boundaries.slice(1).map((boundary, offset) => {
          const index = offset + 1;
          return <button type="button" className="audio-segment-button" style={{ left: `${boundary * 100}%` }} onClick={() => selectTrack(index)} aria-label={t("accessibility.audioTrack", { index: index + 1, title: playlist[index].title })} key={playlist[index].src}><img src={audioDot} alt="" /></button>;
        })}
      </div>
    </div>
    <p className="audio-listening-score" aria-live="polite">{t("audio.listeningScore", { completed: listeningResult.completedTracks, total: listeningResult.totalTracks, score: listeningResult.score, max: listeningResult.maxScore })}</p>
  </section>;
}
